import React from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import { Button } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import { Caption } from 'ui/shorthands/Texts'
import Link from '@mui/material/Link'
import { CallMethodNames, CallMethodVideoAppNames } from 'types/common'

type VideoAppProps = {
  name: string
  shortName: string
  downloadUrl: string
  manualUrl?: string
  registrationUrl?: string
  additionalInfo?: 'ユーザー登録（サインアップ）は必要ありません。'
}

export const videoAppDescription: {
  [key in CallMethodVideoAppNames]: VideoAppProps
} = {
  video_teams: {
    name: 'Microsoft Teams',
    shortName: 'Teams',
    downloadUrl:
      'https://www.microsoft.com/ja-jp/microsoft-365/microsoft-teams/download-app',
    registrationUrl:
      'https://www.microsoft.com/ja-jp/microsoft-teams/group-chat-software',
  },
  video_zoom: {
    name: 'Zoom',
    shortName: 'Zoom',
    downloadUrl: 'https://zoom.us/download',
    manualUrl:
      'https://support.yadoc.jp/manual/medical/quick/preparation/#Zoom',
    additionalInfo: 'ユーザー登録（サインアップ）は必要ありません。',
  },
}

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    display: 'flex',
    padding: theme.spacing(2),
    flexDirection: 'column',
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.primary.light,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    fonSize: theme.spacing(2),
    padding: theme.spacing(1, 0, 0),
    margin: 0,
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1, 0, 0),
  },
  content: {
    paddingLeft: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-end',
      paddingLeft: 0,
    },
  },
  description: {
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  buttonWrapper: {
    flexShrink: 0,
  },
  captionHeader: {
    fontWeight: 'bold',
    paddingBottom: theme.spacing(1),
  },
  linkText: {
    textDecoration: 'none',
    color: theme.palette.primary.main,
  },
}))

export const VideoAppInfo: React.FC<{ app: CallMethodNames }> = ({ app }) => {
  const { t } = useTranslation()
  const { classes } = useStyles()
  if (app === 'phone') {
    return null
  }
  const appProps = videoAppDescription[app]
  return (
    <div className={classes.root}>
      <h3 className={classes.header}>
        <span className={classes.iconWrapper}>
          <InfoIcon color={'primary'} />
        </span>
        {t('アプリのダウンロードが必要です')}
      </h3>
      <div className={classes.content}>
        <div className={classes.description}>
          <Trans
            i18nKey="trans.ビデオ通話には、{appName} アプリが必要です。アプリをお持ちでない方は..."
            values={{
              appName: appProps.name,
            }}
          />
          {appProps.additionalInfo ? t(appProps.additionalInfo) : null}
        </div>
        <div className={classes.buttonWrapper}>
          <Button
            href={appProps.downloadUrl}
            target="_blank"
            rel="nofollow noopener"
            variant={'contained'}
            color={'primary'}
            style={{ textTransform: 'none' }}
          >
            <Trans
              i18nKey="trans.{{appName}}をダウンロード"
              values={{ appName: appProps.shortName }}
            />
          </Button>
        </div>
      </div>
    </div>
  )
}

export const VideoAppSettingCaption: React.FC<{
  app: CallMethodNames
  connected: boolean
}> = ({ app, connected }) => {
  const { classes } = useStyles()
  if (app === 'phone') {
    return null
  }

  const appProps = videoAppDescription[app]
  let caption = (
    <Trans
      i18nKey="trans.{{appName}}アカウントは受付科目ごとに必要です。複数の受付科目を設定する場合..."
      values={{
        appName: appProps.shortName,
      }}
      components={{
        appLink: (
          <Link
            href={appProps.manualUrl}
            target="_blank"
            rel="noopener noreferrer"
          />
        ),
      }}
    />
  )
  if (app === 'video_teams') {
    caption = (
      <>
        <Trans
          i18nKey="trans.Teamsのアカウントをお持ちでない場合は、こちら..."
          components={{
            appLink: (
              <Link
                href={appProps.registrationUrl}
                target="_blank"
                rel="noopener noreferrer"
              />
            ),
          }}
        />
      </>
    )
  }

  return connected ? (
    <>
      <div>
        <Caption className={classes.captionHeader}>
          <Trans
            i18nKey="trans.{{appName}}連携の解除"
            values={{
              appName: appProps.shortName,
            }}
          />
        </Caption>
      </div>
      <Caption>
        <Trans
          i18nKey="trans.「接続解除」ボタンを押すと、{{appName}}との連携を解除できます。<br />全ての予約を通話完了にするか、予約の取消をしてから接続解除ができます。"
          values={{
            appName: appProps.name,
          }}
        />
      </Caption>
    </>
  ) : (
    <Caption>{caption}</Caption>
  )
}
